// Types
import { GetTicketingSetting_getTicketingSetting } from 'api/ticketing/types/GetTicketingSetting';
import { SetTicketingSettingInput } from 'api/graphql-global-types';

type Setting = GetTicketingSetting_getTicketingSetting;

/** The form edits minutes and hours; the backend stores seconds. */
export type TicketingSettingsFormValues = {
  isLaunched: boolean;
  isEnabled: boolean;
  salesEnabled: boolean;
  transfersEnabled: boolean;
  gateEnabled: boolean;
  workersEnabled: boolean;
  holdMinutes: number;
  paymentDeadlineMinutes: number;
  transferWindowHours: number;
  minTicketPrice: number;
  maxTicketPrice: number;
  serviceFeePercent: number;
  serviceFeePerTicket: number;
  defaultEventDurationHours: number;
  defaultSalesCloseAfterStartHours: number;
  maxSalesCloseAfterStartHours: number;
};

const round = (value: number, digits = 2): number =>
  Math.round(value * 10 ** digits) / 10 ** digits;

export const toFormValues = (
  setting: Setting
): TicketingSettingsFormValues => ({
  isLaunched: setting.isLaunched,
  isEnabled: setting.isEnabled,
  salesEnabled: setting.salesEnabled,
  transfersEnabled: setting.transfersEnabled,
  gateEnabled: setting.gateEnabled,
  workersEnabled: setting.workersEnabled,
  holdMinutes: round(setting.holdSeconds / 60),
  paymentDeadlineMinutes: round(setting.paymentResolutionDeadlineSeconds / 60),
  transferWindowHours: setting.transferWindowHours,
  minTicketPrice: setting.minTicketPrice,
  maxTicketPrice: setting.maxTicketPrice,
  serviceFeePercent: setting.serviceFeePercent,
  serviceFeePerTicket: setting.serviceFeePerTicket,
  defaultEventDurationHours: round(setting.defaultEventDurationSeconds / 3600),
  defaultSalesCloseAfterStartHours: round(
    setting.defaultSalesCloseAfterStartSeconds / 3600
  ),
  maxSalesCloseAfterStartHours: round(
    setting.maxSalesCloseAfterStartSeconds / 3600
  ),
});

const toInput = (
  values: TicketingSettingsFormValues
): Required<SetTicketingSettingInput> => ({
  isLaunched: values.isLaunched,
  isEnabled: values.isEnabled,
  salesEnabled: values.salesEnabled,
  transfersEnabled: values.transfersEnabled,
  gateEnabled: values.gateEnabled,
  workersEnabled: values.workersEnabled,
  holdSeconds: Math.round(values.holdMinutes * 60),
  paymentResolutionDeadlineSeconds: Math.round(
    values.paymentDeadlineMinutes * 60
  ),
  transferWindowHours: values.transferWindowHours,
  minTicketPrice: values.minTicketPrice,
  maxTicketPrice: values.maxTicketPrice,
  serviceFeePercent: values.serviceFeePercent,
  serviceFeePerTicket: values.serviceFeePerTicket,
  defaultEventDurationSeconds: Math.round(
    values.defaultEventDurationHours * 3600
  ),
  defaultSalesCloseAfterStartSeconds: Math.round(
    values.defaultSalesCloseAfterStartHours * 3600
  ),
  maxSalesCloseAfterStartSeconds: Math.round(
    values.maxSalesCloseAfterStartHours * 3600
  ),
});

/** Only what changed is sent, so a save never overwrites a value someone else set meanwhile. */
export const getChangedInput = (
  saved: TicketingSettingsFormValues,
  values: TicketingSettingsFormValues
): SetTicketingSettingInput => {
  const before = toInput(saved);
  const after = toInput(values);

  return (
    Object.keys(after) as (keyof SetTicketingSettingInput)[]
  ).reduce<SetTicketingSettingInput>(
    (changes, key) =>
      before[key] === after[key] ? changes : { ...changes, [key]: after[key] },
    {}
  );
};

export const getBuyerTotal = (
  price: number,
  serviceFeePercent: number,
  serviceFeePerTicket: number
): number =>
  round(
    price +
      (price * (serviceFeePercent || 0)) / 100 +
      (serviceFeePerTicket || 0)
  );
